import { ChevronsUpDown, Loader, LogOut } from 'lucide-react'

import { Avatar, AvatarFallback } from '@/components/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/components/sidebar'
import { useSidebar } from '@/components/sidebar.context'
import {
  useLogoutMutation,
  useWhoAmIQuery
} from '@frontend/core/lib/modules/authentication'
import { IMAGE_PRESETS } from '@frontend/core/lib/modules/images'
import { themeAtom } from '@frontend/ui'
import { useAtom } from 'jotai/react'
import { useNavigate } from 'react-router-dom'
import { StaticAssetImage } from '../modules/images/components'

const textData = {
  theme: {
    en: 'Theme',
    fr: 'Thème'
  },
  lightTheme: {
    en: 'Light',
    fr: 'Clair'
  },
  darkTheme: {
    en: 'Dark',
    fr: 'Sombre'
  },
  language: {
    en: 'Language',
    fr: 'Langue'
  }
}

export function SidebarNavUser() {
  const { data: user } = useWhoAmIQuery()

  const [logout, { isLoading: isLogoutLoading }] = useLogoutMutation()
  const { isMobile } = useSidebar()
  const navigate = useNavigate()
  const [theme, setTheme] = useAtom(themeAtom)

  const language = 'en'

  if (!user) {
    return null
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <Avatar className='h-8 w-8 rounded-lg'>
                <StaticAssetImage
                  src={user.image}
                  alt={user.firstName}
                  preset={IMAGE_PRESETS.playerThumbnail}
                  fallback={
                    <AvatarFallback className='rounded-lg'>
                      {user.firstName.charAt(0)}
                    </AvatarFallback>
                  }
                />
              </Avatar>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-medium'>
                  {user.firstName} {user.lastName}
                </span>
                <span className='truncate text-xs'>{user.email}</span>
              </div>
              <ChevronsUpDown className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel
              onClick={() => {
                navigate('/profile')
              }}
              className='cursor-pointer p-0 font-normal'
            >
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  <StaticAssetImage
                    src={user.image}
                    alt={user.firstName}
                    preset={IMAGE_PRESETS.playerThumbnail}
                    fallback={
                      <AvatarFallback className='rounded-lg'>
                        {user.firstName.charAt(0)}
                      </AvatarFallback>
                    }
                  />
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-medium'>
                    {user.firstName} {user.lastName}
                  </span>
                  <span className='truncate text-xs'>{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className='font-normal'>
              {textData.theme[language]}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={theme}
              onValueChange={(value) => {
                if (value === 'light' || value === 'dark') setTheme(value)
              }}
            >
              <DropdownMenuRadioItem value='light'>
                {textData.lightTheme[language]}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value='dark'>
                {textData.darkTheme[language]}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className='font-normal'>
              {textData.language[language]}
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup value={'en'}>
              <DropdownMenuRadioItem value='en'>EN</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                if (isLogoutLoading) return
                logout()
              }}
            >
              {isLogoutLoading ? (
                <>
                  <Loader className='animate-spin' />
                  Logging out...
                </>
              ) : (
                <>
                  <LogOut />
                  Log out
                </>
              )}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
